import {
    useEffect,
    useRef,
    useState
} from 'react';

import {
    disableBodyScroll,
    enableBodyScroll
} from 'body-scroll-lock';

import { useKeyboard } from '$hooks/events.js';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

export const useDialog = ({ exitDelay = 0 }) => {
    const dialogRef = useRef(undefined);
    const scrollRef = useRef(undefined);

    const [isOpen, setIsOpen] = useState(false);

    const handleOpen = () => setIsOpen(true);
    const handleClose = () => setIsOpen(false);

    useKeyboard({ 'Escape': () => setIsOpen(false) }, [isOpen]);

    useEffect(() => {
        if (dialogRef.current) {
            (async () => {
                if (typeof HTMLDialogElement !== 'function') {
                    const { default: dialogPolyfill } = await import('dialog-polyfill');
                    dialogPolyfill.registerDialog(dialogRef.current);
                    dialogRef.current.showModal();
                }
            })();
        }
    }, [dialogRef.current]);

    useEffect(() => {
        let closeTimeout;
        const dialogNode = dialogRef.current;

        if (isOpen) {
            if (dialogNode && !dialogNode.open) {
                if (scrollRef.current) {
                    disableBodyScroll(scrollRef.current);
                }

                dialogNode.showModal?.();
            }
        } else {
            if (dialogNode && dialogNode.open) {
                const close = () => {
                    if (scrollRef.current) {
                        enableBodyScroll(scrollRef.current);
                    }

                    dialogNode.close?.();
                };

                if (exitDelay) {
                    closeTimeout = setTimeout(close, exitDelay);
                } else {
                    close();
                }
            }
        }

        return () => {
            if (scrollRef.current) {
                enableBodyScroll(scrollRef.current);
            }

            if (closeTimeout) {
                clearTimeout(closeTimeout);
            }
        };
    }, [
        isOpen,
        dialogRef.current,
        scrollRef.current
    ]);

    return {
        dialogRef,
        handleClose,
        handleOpen,
        isOpen,
        scrollRef
    };
};
